import type { ProductsDict } from './products.types';

type ProductsCompactMetricsProps = {
  dict?: ProductsDict;
  totalProducts: number;
  totalScans30d: number;
  isLoading?: boolean;
};

const formatInteger = (value: number): string =>
  new Intl.NumberFormat().format(value);

export function scansLabelForCount(
  count: number,
  dict?: ProductsDict,
  variant: 'metrics' | 'cardShort' | 'cardLong' = 'metrics',
): string {
  if (variant === 'cardShort') {
    if (count === 1) {
      return (
        dict?.list?.cardMeta?.scans30dShortOne ??
        dict?.list?.cardMeta?.scans30dShort ??
        'Scan'
      );
    }
    return (
      dict?.list?.cardMeta?.scans30dShortMany ??
      dict?.list?.cardMeta?.scans30dShort ??
      'Scans'
    );
  }
  if (variant === 'cardLong') {
    if (count === 1) {
      return (
        dict?.list?.cardMeta?.scans30dOne ??
        dict?.list?.cardMeta?.scans30d ??
        'Scan in the last 30 days'
      );
    }
    return (
      dict?.list?.cardMeta?.scans30dMany ??
      dict?.list?.cardMeta?.scans30d ??
      'Scans in the last 30 days'
    );
  }
  if (count === 1) {
    return (
      dict?.metrics?.totalScans30d?.labelOne ??
      dict?.metrics?.totalScans30d?.label ??
      dict?.list?.cardMeta?.scans30dOne ??
      'Scan in the last 30 days'
    );
  }
  return (
    dict?.metrics?.totalScans30d?.labelMany ??
    dict?.metrics?.totalScans30d?.label ??
    dict?.list?.cardMeta?.scans30dMany ??
    'Scans in the last 30 days'
  );
}

/**
 * Dense inventory summary for the products list — not a metric-card wall.
 * Configurators need a quick count + scan pulse, then the list.
 */
export function ProductsCompactMetrics({
  dict,
  totalProducts,
  totalScans30d,
  isLoading,
}: ProductsCompactMetricsProps) {
  // DEV-536: "1 Produkt", not "1 Produkte".
  const productsLabel =
    totalProducts === 1
      ? (dict?.list?.productCountOne ?? 'product')
      : (dict?.list?.productCount ?? 'products');
  const scansLabel = scansLabelForCount(totalScans30d, dict, 'metrics');

  if (isLoading) {
    return (
      <p
        className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13.5px] leading-relaxed text-muted-foreground"
        aria-busy="true"
      >
        <span className="inline-block h-4 w-24 animate-pulse rounded bg-muted motion-reduce:animate-none" />
        <span className="text-border" aria-hidden>
          ·
        </span>
        <span className="inline-block h-4 w-28 animate-pulse rounded bg-muted motion-reduce:animate-none" />
      </p>
    );
  }

  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13.5px] leading-relaxed text-muted-foreground">
      <span>
        <span className="font-semibold tabular-nums text-foreground">
          {formatInteger(totalProducts)}
        </span>{' '}
        {productsLabel}
      </span>
      <span className="text-border" aria-hidden>
        ·
      </span>
      <span>
        <span className="font-semibold tabular-nums text-foreground">
          {formatInteger(totalScans30d)}
        </span>{' '}
        {scansLabel}
      </span>
    </p>
  );
}
